import { useEffect, useRef, useState } from 'react';
import { usePulseStore } from './usePulseStore';

/** How often to rotate which videos are playing (ms). */
const ROTATION_INTERVAL_MS = 12_000;

/** Fraction of video items that play simultaneously. */
const PLAY_FRACTION = 0.1;

/**
 * Controls which video cells are actively playing.
 * Only ~10% of videos play at any one time; the rest show their poster thumbnail.
 * Every 12 seconds, a new random subset is selected to play.
 * This reduces visual busyness and network/CPU load.
 */
export function usePulsePlayback(): Set<string> {
  const [playingIds, setPlayingIds] = useState<Set<string>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const rotate = () => {
      const { items } = usePulseStore.getState();
      const videoItems = items.filter((i) => !!i.playbackUrl);
      if (videoItems.length === 0) {
        setPlayingIds(new Set());
        return;
      }

      const count = Math.max(1, Math.ceil(videoItems.length * PLAY_FRACTION));

      // Pick `count` random video items to play.
      const shuffled = [...videoItems].sort(() => Math.random() - 0.5);
      const selected = new Set(shuffled.slice(0, count).map((i) => i.contentId));
      setPlayingIds(selected);
    };

    // Initial selection after a brief delay (let grid populate first).
    const initialTimer = setTimeout(rotate, 1500);
    intervalRef.current = setInterval(rotate, ROTATION_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return playingIds;
}
